import React, { useId } from 'react';
import { cn } from '@/lib/utils';
import { BRAND } from '@/lib/brand';
import { CookieBeesWordmark } from '@/components/brand/cookiebees-wordmark';

/**
 * The Cookiebees mark: the bee itself. No comb cell around it - the hexagon
 * made the bee a quarter of the mark's area, and at sidebar size it read as a
 * badge with a smudge in it. The bee now fills the whole box.
 *
 * Drawn inline rather than loaded from /logo.png, for two reasons the fork hit
 * in production:
 *
 *   1. The app is served under /app/, so a root-absolute "/logo.png" 301s to
 *      the SPA shell and the browser gets HTML where it wanted an image. Every
 *      tenant saw a broken-image icon.
 *   2. The file was still the Measure Marketer wordmark, inherited at the fork -
 *      so fixing the path alone would have shown the wrong company's logo.
 *
 * An inline SVG has no path to get wrong, no request to fail, and scales to any
 * size. It also lets the antennae ANIMATE - twice vertically, twice horizontally,
 * once on load and again on hover - which a PNG or a GIF cannot do crisply on a
 * transparent background at 32px. Keep it inline.
 */

// The 2026 brand book's bee (owner, 17 Oct 2026: "the new bee logo looks good
// than the one we have"): the same bee as before - cookie head with three chips,
// honey body, three stripes, two wings, a slight tilt - drawn flatter, with one
// honey and one ink. Deliberately NOT currentColor: it should look like itself
// everywhere. The marketing site draws the same bee (marketing/components/
// BeeMark.jsx); a change to one is made in both.
const HONEY = '#E5A70B';
const INK = '#1C140B';
const CREAM = '#F7EFE2';
const WHITE = '#FFFFFF';
const DOUGH = '#EED3A2';
const RIM = '#D9AE68';
const CHIP = '#5A3B16';

/*
 * The antenna wiggle: two vertical nods, then two horizontal sways, then the
 * wings flutter, on one shared 2.8s clock whose second half is rest, so the bee
 * reads as alive rather than agitated. Sides mirror each other.
 *
 * transform-box: fill-box pins each part's rotation to its own box; the origins
 * sit where each antenna and wing meets the body, so they flex from the root.
 * The <style> is injected per-instance but the rules are identical, so
 * duplicates are harmless; prefers-reduced-motion turns the whole thing off.
 */
const ANTENNA_CSS = `
@media (prefers-reduced-motion: no-preference) {
  .cb-ant { transform-box: fill-box; }
  .cb-ant-r { transform-origin: 10% 95%; animation: cbAntR 2.8s ease-in-out .4s infinite; }
  .cb-ant-l { transform-origin: 90% 95%; animation: cbAntL 2.8s ease-in-out .4s infinite; }
  @keyframes cbAntR {
    0%, 40%, 100% { transform: none; }
    5%, 15% { transform: translateY(-7%); }
    10%, 20% { transform: none; }
    50%, 70% { transform: rotate(12deg); }
    60%, 80% { transform: rotate(-6deg); }
  }
  @keyframes cbAntL {
    0%, 40%, 100% { transform: none; }
    5%, 15% { transform: translateY(-7%); }
    10%, 20% { transform: none; }
    50%, 70% { transform: rotate(-12deg); }
    60%, 80% { transform: rotate(6deg); }
  }
  .cb-wing { transform-box: fill-box; }
  .cb-wing-r { transform-origin: 5% 18%; animation: cbWingR 2.8s ease-in-out .4s infinite; }
  .cb-wing-l { transform-origin: 95% 18%; animation: cbWingL 2.8s ease-in-out .4s infinite; }
  @keyframes cbWingR {
    0%, 24%, 46%, 100% { transform: none; }
    27%, 35%, 43% { transform: rotate(10deg); }
    31%, 39% { transform: rotate(-4deg); }
  }
  @keyframes cbWingL {
    0%, 24%, 46%, 100% { transform: none; }
    27%, 35%, 43% { transform: rotate(-10deg); }
    31%, 39% { transform: rotate(4deg); }
  }
}`;

// The bee itself, shared by every variant, in a 220 box. `fine` adds the wings,
// antennae and chips (noise below ~20px); `dark` turns the antennae white and
// the wings cream for dark grounds, the job the old honey disc used to do. With
// `auto` (the default) the antennae are ink and turn white in the app's dark
// mode: a class beats the stroke/fill attribute, so the theme decides (owner,
// 17 Oct 2026: on black the ink antennae were invisible).
function BeeBody({ uid, fine, animate, dark, auto }) {
  const line = dark ? WHITE : INK;
  const lineCls = auto ? 'dark:stroke-white' : undefined;
  const dotCls = auto ? 'dark:fill-white' : undefined;
  return (
    <g transform="rotate(-10 110 115)">
      {fine && [-1, 1].map((d) => (
        <g key={`a${d}`} className={animate ? `cb-ant ${d > 0 ? 'cb-ant-r' : 'cb-ant-l'}` : undefined}>
          <path d={d > 0 ? 'M120 50Q130 30 146 24' : 'M100 50Q90 30 74 24'} fill="none" stroke={line} strokeWidth="7" strokeLinecap="round" className={lineCls} />
          <circle cx={d > 0 ? 148 : 72} cy="23" r="8.5" fill={line} className={dotCls} />
        </g>
      ))}
      {fine && [-1, 1].map((d) => (
        <g key={`w${d}`} className={animate ? `cb-wing ${d > 0 ? 'cb-wing-r' : 'cb-wing-l'}` : undefined}>
          <ellipse cx={d > 0 ? 156 : 64} cy="94" rx="45" ry="25" transform={`rotate(${d * 28} ${d > 0 ? 156 : 64} 94)`}
            fill={dark ? CREAM : '#FFFDF8'} fillOpacity={dark ? 0.92 : 1}
            stroke={dark ? 'none' : INK} strokeOpacity=".3" strokeWidth="6" />
        </g>
      ))}
      <defs>
        <clipPath id={`${uid}-b`}>
          <ellipse cx="110" cy="148" rx="44" ry="54" />
        </clipPath>
      </defs>
      <ellipse cx="110" cy="148" rx="44" ry="54" fill={HONEY} />
      <g clipPath={`url(#${uid}-b)`} fill={INK}>
        <rect x="60" y="136" width="100" height="13" />
        <rect x="60" y="160" width="100" height="13" />
        <rect x="60" y="184" width="100" height="13" />
      </g>
      <path d="M103 199L110 213L117 199Z" fill={dark ? HONEY : INK} />
      <ellipse cx="110" cy="104" rx="31" ry="21" fill={HONEY} />
      <circle cx="110" cy="70" r="27" fill={DOUGH} stroke={RIM} strokeWidth="4" />
      {fine && (
        <g fill={CHIP}>
          <circle cx="101" cy="64" r="5.2" />
          <circle cx="119" cy="67" r="4.8" />
          <circle cx="107" cy="80" r="4.4" />
        </g>
      )}
    </g>
  );
}

/**
 * MMLogoMark - the square brand mark. Collapsed sidebar, agency console header,
 * anywhere a favicon-shaped icon is needed.
 *
 * Below ~20px the antennae and wing outlines turn into noise, so `detail` drops
 * them (which also drops the animation - nothing to move). `animate` can force
 * the wiggle off for contexts where motion would be wrong.
 */
export function MMLogoMark({ className, size = 28, detail, animate = true, cookieHead, tone = 'auto', ...props }) {
  const uid = useId().replace(/:/g, '');
  const fine = detail ?? size >= 20;
  // `cookieHead` is accepted for old call sites: every bee has its cookie head
  // now. There is no disc or circle behind the bee, ever (owner, 23 Oct 2026:
  // one logo; memory feedback_one_logo).
  return (
    <svg
      viewBox="0 0 220 220"
      width={size}
      height={size}
      className={cn('cb-bee shrink-0', className)}
      style={{ overflow: 'visible' }}
      role="img"
      aria-label={BRAND.name}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {fine && animate && <style>{ANTENNA_CSS}</style>}
      <BeeBody uid={uid} fine={fine} animate={fine && animate} dark={tone === 'dark'} auto={tone === 'auto'} />
    </svg>
  );
}

/**
 * BeeGlyph - kept as its own export because call sites (the pinned CID row on
 * the order page) chose it for "bee without the box", which is now what the
 * mark IS. Same drawing; it animates too (owner's call - the wiggle is gentle
 * enough to sit next to data).
 */
export function BeeGlyph({ className, size = 18, detail, animate = true, ...props }) {
  return <MMLogoMark className={className} size={size} detail={detail} animate={animate} {...props} />;
}

/**
 * MMLogo - the horizontal lockup: mark plus wordmark.
 *
 * The wordmark is live text rather than a baked image, so it stays sharp at any
 * zoom, reads to a screen reader, and follows the theme's foreground colour
 * instead of being a white-background PNG sitting on a dark card. Call sites
 * already pass h-10 / h-7, which still sizes the row.
 */
export function MMLogo({ className, markSize = 30, ...props }) {
  return (
    <span
      className={cn('inline-flex select-none items-center gap-1 text-foreground', className)}
      {...props}
    >
      <MMLogoMark size={markSize} />
      {/* The real wordmark, not the name set in the interface font. Typing the
          name beside the mark is what made every header look unbranded - it is
          the name, not the logo. One definition here so no page can drift. */}
      <CookieBeesWordmark className="h-[1.1em] w-auto" />
    </span>
  );
}
